import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LayoutDashboard, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useReaderAuth } from '../context/ReaderAuthContext';
import { TCLBannerLogo } from './TCLMark';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const NAVY = '#071B3A';
const GOLD = '#C99316';

// ── Visitor local clock ───────────────────────────────────────────────────────
const LocalClock = () => {
  const [time, setTime] = useState('');
  const [tz, setTz] = useState('');

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setTime(now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }));
      // Short timezone name e.g. "BST", "EST", "WAT"
      setTz(now.toLocaleTimeString('en-GB', { timeZoneName: 'short' }).split(' ').pop());
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  if (!time) return null;
  return (
    <div
      className="hidden lg:flex items-center gap-1.5 font-mono select-none"
      style={{ color: 'rgba(7,27,58,0.35)', fontSize: '11px', letterSpacing: '0.06em' }}
      data-testid="local-clock"
      title={`Your local time — ${Intl.DateTimeFormat().resolvedOptions().timeZone}`}
    >
      <span style={{ color: 'rgba(7,27,58,0.55)', fontVariantNumeric: 'tabular-nums' }}>{time}</span>
      <span style={{ color: 'rgba(7,27,58,0.2)', fontSize: '9px', letterSpacing: '0.12em', textTransform: 'uppercase' }}>{tz}</span>
    </div>
  );
};

// ── Reader-first nav auth button ─────────────────────────────────────────────
const NavAuthSection = () => {
  const { isAuthenticated: isReaderAuth, user: readerUser, logout: readerLogout } = useReaderAuth();
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loggedIn = typeof isReaderAuth === 'function' ? isReaderAuth() : false;

  if (!loggedIn) {
    return (
      <Link
        to="/signin"
        className="text-[11px] uppercase tracking-[0.18em] font-sans font-medium px-4 py-2 transition-all duration-300 hover:bg-[#071B3A] hover:text-[#FAF8F3]"
        style={{ border: '1px solid rgba(7,27,58,0.22)', color: 'rgba(7,27,58,0.62)' }}
        data-testid="nav-signin-btn"
      >
        Sign In
      </Link>
    );
  }

  const initials = readerUser?.first_name
    ? readerUser.first_name[0].toUpperCase()
    : readerUser?.email?.[0]?.toUpperCase() ?? '?';

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 transition-all duration-200"
        data-testid="nav-reader-avatar"
        aria-label="Reader menu"
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-sans font-semibold"
          style={{ backgroundColor: GOLD + '18', border: '1px solid ' + GOLD + '55', color: NAVY }}
        >
          {initials}
        </div>
        <ChevronDown
          size={12}
          className="transition-transform duration-200"
          style={{ color: 'rgba(7,27,58,0.4)', transform: open ? 'rotate(180deg)' : 'rotate(0)' }}
        />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-52 z-50"
          style={{ backgroundColor: 'white', border: '1px solid rgba(7,27,58,0.09)', boxShadow: '0 8px 32px rgba(7,27,58,0.10)' }}
        >
          <div className="px-4 py-3" style={{ borderBottom: '1px solid rgba(7,27,58,0.07)' }}>
            <p className="font-sans text-xs font-medium truncate" style={{ color: NAVY }}>
              {readerUser?.first_name ? `${readerUser.first_name} ${readerUser.last_name || ''}`.trim() : 'Reader'}
            </p>
            <p className="font-sans text-[11px] truncate" style={{ color: 'rgba(7,27,58,0.4)' }}>
              {readerUser?.email}
            </p>
          </div>
          <div className="p-1.5">
            <Link
              to="/dashboard"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-3 py-2 text-[11px] uppercase tracking-[0.15em] font-sans transition-colors duration-150 hover:bg-[#071B3A]/5"
              style={{ color: 'rgba(7,27,58,0.65)' }}
              data-testid="nav-reader-dashboard"
            >
              <LayoutDashboard size={13} />
              My Dashboard
            </Link>
            {isAdmin?.() && (
              <Link
                to="/admin-panel"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 px-3 py-2 text-[11px] uppercase tracking-[0.15em] font-sans transition-colors hover:bg-[#071B3A]/5"
                style={{ color: GOLD + 'CC' }}
              >
                Admin Panel
              </Link>
            )}
            <button
              onClick={() => { readerLogout(); setOpen(false); }}
              className="flex items-center gap-2.5 w-full px-3 py-2 text-[11px] uppercase tracking-[0.15em] font-sans transition-colors hover:bg-red-50 text-left"
              style={{ color: '#C0392B99' }}
              data-testid="nav-reader-signout"
            >
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Map nav item paths to toggle keys
const PATH_TO_TOGGLE = {
  '/books': 'books',
  '/articles': 'articles',
  '/speaking': 'speaking',
  '/podcast': 'podcast',
  '/social': 'social',
  '/about': 'about',
  '/cfq-assessment': 'cfq',
  '/courses': 'courses',
  '/organisations': 'organisations',
  '/contact': 'contact',
  '/pillars': 'pillars',
  '/for-speakers': 'for-speakers',
};

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [toggles, setToggles] = useState(null);
  const location = useLocation();
  const isAdminPanel = location.pathname === '/admin-panel';
  const { isAuthenticated: isReaderAuth } = useReaderAuth();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  // Fetch page toggles once
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/site/page-toggles`);
        if (res.ok) {
          const data = await res.json();
          setToggles(data.toggles || {});
        }
      } catch {}
    })();
  }, []);

  if (isAdminPanel) return null;

  const isPageEnabled = (path) => {
    if (!toggles) return true; // Show all while loading
    const key = PATH_TO_TOGGLE[path];
    if (!key) return true; // Pages without a toggle are always visible
    return toggles[key] !== false;
  };

  const allNavItems = [
    { name: 'Home', path: '/' },
    { name: 'Books', path: '/books' },
    { name: 'Articles', path: '/articles' },
    { name: 'Speaking', path: '/speaking' },
    { name: 'Courses', path: '/courses' },
    { name: 'About', path: '/about' },
    { name: 'CFQ', path: '/cfq-assessment', highlight: true },
  ];

  const allMobileExtraLinks = [
    { name: 'Podcast', path: '/podcast' },
    { name: 'Social', path: '/social' },
    { name: 'Organisations', path: '/organisations' },
    { name: 'Cultural Fluency Matrix™', path: '/cfq-matrix' },
    { name: 'Contact', path: '/contact' },
    { name: 'Pillars', path: '/pillars' },
    { name: 'Promond Publishing', path: '/promond-publishing' },
    { name: 'Bio Links', path: '/bio-links' },
  ];

  const navItems = allNavItems.filter(item => isPageEnabled(item.path));
  const mobileExtraLinks = allMobileExtraLinks.filter(item => isPageEnabled(item.path));

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          backgroundColor: 'rgba(250,248,243,0.97)',
          borderBottom: '1px solid rgba(7,27,58,0.08)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          boxShadow: isScrolled ? '0 1px 16px rgba(7,27,58,0.07)' : 'none',
        }}
        role="navigation"
        aria-label="Main navigation"
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="flex items-center justify-between h-[76px]">
            <Link to="/" className="flex items-center" data-testid="nav-logo">
              {/* Exact brand asset — blends seamlessly on ivory nav background */}
              <TCLBannerLogo height={46} />
            </Link>

            <div className="hidden lg:flex items-center gap-7">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-link-${item.name.toLowerCase()}`}
                  className={`relative text-[12px] uppercase tracking-[0.18em] font-medium transition-colors duration-300 pb-0.5 ${
                    isActive(item.path)
                      ? 'text-[#C99316]'
                      : 'text-[#071B3A]/68 hover:text-[#071B3A]'
                  } after:absolute after:bottom-0 after:left-0 after:w-0 after:h-[1.5px] after:bg-[#C99316] after:transition-all after:duration-300 hover:after:w-full ${
                    isActive(item.path) ? 'after:w-full' : ''
                  } ${item.highlight ? 'border border-[#C99316]/50 px-3 py-1 text-[#C99316] hover:bg-[#C99316] hover:text-[#071B3A] transition-all duration-300 after:hidden' : ''}`}
                >
                  {item.name}
                </Link>
              ))}
              <div className="ml-2 flex items-center gap-4">
                <LocalClock />
                <NavAuthSection />
              </div>
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-3 -mr-3 touch-manipulation"
              data-testid="mobile-menu-toggle"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? (
                <X size={24} className="text-[#C99316]" />
              ) : (
                <Menu size={24} className="text-[#071B3A]/70" />
              )}
            </button>
          </div>
        </div>
      </nav>

      <div
        className={`fixed inset-0 z-40 transition-all duration-500 lg:hidden ${
          isMobileMenuOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
        style={{ backgroundColor: '#071B3A' }}
        data-testid="mobile-menu"
      >
        <div className="h-full overflow-y-auto pt-24 pb-10 px-8">
          <nav className="flex flex-col items-center gap-5">
            {navItems.map((item, i) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                className={`font-serif text-2xl sm:text-3xl transition-all duration-300 ${
                  isActive(item.path) ? 'text-[#C99316]' : 'text-[#FAF8F3]/75 hover:text-[#C99316]'
                } ${item.highlight ? 'border border-[#C99316]/40 px-5 py-2 text-[#C99316] text-xl' : ''}`}
                style={{ animationDelay: i * 60 + 'ms' }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="w-16 h-px mx-auto my-6" style={{ backgroundColor: 'rgba(201,147,22,0.2)' }} />
          <div className="flex flex-col items-center gap-4">
            {mobileExtraLinks.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm uppercase tracking-[0.15em] transition-colors ${
                  isActive(item.path) ? 'text-[#C99316]' : 'text-[#FAF8F3]/55 hover:text-[#C99316]'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
          </div>
          <div className="mt-8 flex flex-col items-center gap-4">
            <NavAuthSection />
            <Link
              to="/signup"
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-xs uppercase tracking-[0.2em] font-sans"
              style={{ color: 'rgba(250,248,243,0.50)' }}
            >
              New? Create an account
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
